import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiCalendar, FiChevronDown, FiChevronRight, FiClipboard, FiFileText, FiInfo, FiMapPin } from 'react-icons/fi';
import { apiUrl, fileUrl } from '../utils/api';
import { AuthContext } from '../context/AuthContext';

const CASE_TABLE_LAYOUT_STORAGE_KEY = 'inta-case-table-layout-v2';
const CASE_TABLE_COLUMNS = [
  { id: 'toggle', label: '', width: 52, minWidth: 52, className: 'case-table__toggle-col', resizable: false, draggable: false, removable: false },
  { id: 'view', label: 'Ver caso', width: 92, minWidth: 82 },
  { id: 'protocol', label: 'Protocolo', width: 102, minWidth: 92 },
  { id: 'caseDate', label: 'Fecha del caso', width: 108, minWidth: 98 },
  { id: 'entryDate', label: 'Fecha de ingreso', width: 116, minWidth: 102 },
  { id: 'owner', label: 'Propietario', width: 146, minWidth: 124 },
  { id: 'species', label: 'Grupo zootécnico', width: 144, minWidth: 120 },
  { id: 'primaryDiagnosis', label: 'Diagnóstico primario', width: 140, minWidth: 120 },
  { id: 'secondaryDiagnosis', label: 'Diagnóstico secundario', width: 146, minWidth: 124 },
  { id: 'province', label: 'Provincia', width: 104, minWidth: 92 },
  { id: 'locality', label: 'Localidad', width: 104, minWidth: 92 },
  { id: 'address', label: 'Dirección', width: 156, minWidth: 132 },
  { id: 'doctor', label: 'Médico responsable', width: 140, minWidth: 120 },
  { id: 'finalReport', label: 'Diagnóstico e informe final', width: 156, minWidth: 136 },
  { id: 'documents', label: 'Documentos', width: 154, minWidth: 132 },
  { id: 'anamnesis', label: 'Antecedentes / Anamnesis', width: 190, minWidth: 150 },
  { id: 'actions', label: 'Acciones', width: 132, minWidth: 116 },
];

const DEFAULT_COLUMN_ORDER = CASE_TABLE_COLUMNS.map((column) => column.id);
const DEFAULT_COLUMN_WIDTHS = CASE_TABLE_COLUMNS.reduce((accumulator, column) => {
  accumulator[column.id] = column.width;
  return accumulator;
}, {});

const moveColumn = (columns, sourceId, targetId) => {
  if (!sourceId || !targetId || sourceId === targetId) {
    return columns;
  }

  const sourceIndex = columns.indexOf(sourceId);
  const targetIndex = columns.indexOf(targetId);

  if (sourceIndex === -1 || targetIndex === -1) {
    return columns;
  }

  const nextColumns = [...columns];
  const [movedColumn] = nextColumns.splice(sourceIndex, 1);
  nextColumns.splice(targetIndex, 0, movedColumn);
  return nextColumns;
};

const readStoredTableLayout = () => {
  try {
    const rawValue = window.localStorage.getItem(CASE_TABLE_LAYOUT_STORAGE_KEY);
    if (!rawValue) {
      return {
        order: DEFAULT_COLUMN_ORDER,
        widths: DEFAULT_COLUMN_WIDTHS,
      };
    }

    const parsedValue = JSON.parse(rawValue);
    const storedOrder = Array.isArray(parsedValue?.order) ? parsedValue.order : [];
    const knownColumns = new Set(DEFAULT_COLUMN_ORDER);
    const order = [
      ...storedOrder.filter((columnId) => knownColumns.has(columnId)),
      ...DEFAULT_COLUMN_ORDER.filter((columnId) => !storedOrder.includes(columnId)),
    ];

    const widths = CASE_TABLE_COLUMNS.reduce((accumulator, column) => {
      const storedWidth = Number(parsedValue?.widths?.[column.id]);
      accumulator[column.id] = Number.isFinite(storedWidth)
        ? Math.max(column.minWidth, Math.round(storedWidth))
        : column.width;
      return accumulator;
    }, {});

    return { order, widths };
  } catch (error) {
    return {
      order: DEFAULT_COLUMN_ORDER,
      widths: DEFAULT_COLUMN_WIDTHS,
    };
  }
};

const parseDate = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateLabel = (value) => {
  const parsed = parseDate(value);
  if (!parsed) {
    return 'Sin fecha';
  }

  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
};

const formatDateTimeLabel = (value) => {
  if (!value) {
    return 'Sin fecha';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Sin fecha';
  }

  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const FilterHelp = ({ description }) => (
  <span className="info-tooltip case-filter-help">
    <span className="info-badge" tabIndex={0} aria-label={description}>
      <FiInfo aria-hidden="true" />
    </span>
    <span className="info-tooltip__content" role="tooltip">
      {description}
    </span>
  </span>
);

const CaseStatCard = ({ label, value, detail, icon: Icon }) => (
  <article className="case-stat-card">
    <div className="case-stat-card__head">
      <span>{label}</span>
      <span className="case-stat-card__icon">
        <Icon aria-hidden="true" />
      </span>
    </div>
    <strong className="case-stat-card__value">{value}</strong>
    {detail ? <span className="case-stat-card__detail">{detail}</span> : null}
  </article>
);

const INITIAL_FILTERS = {
  protocolo: '',
  propietario: '',
  diagnostico: '',
  desde: '',
  hasta: '',
  provincia: '',
  localidad: '',
  medico: '',
  pendientesEntrega: false,
};

const truncateText = (value, maxLength = 80) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) {
    return '';
  }

  if (normalizedValue.length <= maxLength) {
    return normalizedValue;
  }

  return `${normalizedValue.slice(0, maxLength - 1)}…`;
};

const getFileName = (path = '') => {
  const cleanPath = String(path || '').split('?')[0];
  const segments = cleanPath.split('/');
  return segments[segments.length - 1] || 'archivo';
};

const getCaseDocuments = (caso) => {
  const caseDocuments = Array.isArray(caso?.documentos) ? [...caso.documentos] : [];

  if (caso?.archivo_caso) {
    caseDocuments.unshift({
      id: `informe-final-${caso.protocolo_general}`,
      archivo: caso.archivo_caso,
      nombre: 'Informe final',
      descripcion: 'Informe final',
    });
  }

  return caseDocuments;
};

const getCaseSpecies = (caso) => {
  const species = Array.isArray(caso?.muestras)
    ? caso.muestras
      .map((muestra) => muestra?.grupo_zootecnico)
      .filter(Boolean)
    : [];

  const uniqueSpecies = [...new Set(species)];
  if (uniqueSpecies.length === 0) {
    return 'Sin especie';
  }

  return uniqueSpecies.join(', ');
};

const ListaCasos = () => {
  const { usuario } = useContext(AuthContext);
  const storedLayout = useMemo(() => readStoredTableLayout(), []);
  const [casos, setCasos] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    provincias: [],
    localidades: [],
    medicos: [],
  });
  const [caseSummary, setCaseSummary] = useState({
    totalConMuestras: 0,
    totalDocumentos: 0,
    totalProvincias: 0,
  });
  const [totalCases, setTotalCases] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draftFilters, setDraftFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedCases, setExpandedCases] = useState({});
  const [deletingCaseId, setDeletingCaseId] = useState('');
  const [columnOrder, setColumnOrder] = useState(storedLayout.order);
  const [columnWidths, setColumnWidths] = useState(storedLayout.widths);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [draggedColumnId, setDraggedColumnId] = useState('');
  const resizeStateRef = useRef(null);
  const canDeleteRecords = usuario?.role === 'admin' || Boolean(usuario?.is_superuser) || Boolean(usuario?.eliminacion_autorizada);
  const showActionsColumn = true;
  const token = localStorage.getItem('token');

  useEffect(() => {
    let active = true;

    axios
      .get(apiUrl('/api/ingresos_casos/filtros/'))
      .then((response) => {
        if (!active) {
          return;
        }

        setFilterOptions({
          provincias: response.data.provincias || [],
          localidades: response.data.localidades || [],
          medicos: response.data.medicos || [],
        });
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    axios
      .get(apiUrl('/api/ingresos_casos/filtros/'), {
        params: {
          provincia: draftFilters.provincia || undefined,
        },
      })
      .then((response) => {
        if (!active) {
          return;
        }

        setFilterOptions((current) => ({
          ...current,
          localidades: response.data.localidades || [],
        }));
      })
      .catch((fetchError) => {
        console.error('Error al obtener localidades:', fetchError);
      });

    return () => {
      active = false;
    };
  }, [draftFilters.provincia]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    axios
      .get(apiUrl('/api/ingresos_casos/'), {
        params: {
          paginated: 1,
          page: currentPage,
          page_size: rowsPerPage,
          protocolo: appliedFilters.protocolo || undefined,
          propietario: appliedFilters.propietario || undefined,
          diagnostico: appliedFilters.diagnostico || undefined,
          desde: appliedFilters.desde || undefined,
          hasta: appliedFilters.hasta || undefined,
          provincia: appliedFilters.provincia || undefined,
          localidad: appliedFilters.localidad || undefined,
          medico_responsable: appliedFilters.medico || undefined,
          pendientes_entrega: appliedFilters.pendientesEntrega ? 1 : undefined,
        },
      })
      .then((response) => {
        if (!active) {
          return;
        }

        const payload = response.data || {};
        setCasos(payload.results?.results || []);
        setTotalCases(payload.count || 0);
        setCaseSummary({
          totalConMuestras: payload.results?.summary?.total_con_muestras || 0,
          totalDocumentos: payload.results?.summary?.total_documentos || 0,
          totalProvincias: payload.results?.summary?.total_provincias || 0,
        });
      })
      .catch((fetchError) => {
        if (!active) {
          return;
        }

        console.error('Error al obtener casos:', fetchError);
        setError('No se pudieron cargar los casos.');
        setCasos([]);
        setTotalCases(0);
        setCaseSummary({
          totalConMuestras: 0,
          totalDocumentos: 0,
          totalProvincias: 0,
        });
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [appliedFilters, currentPage, rowsPerPage]);

  const descargarArchivo = (url) => {
    if (!url) {
      window.alert('URL de documento no disponible');
      return;
    }

    const finalUrl = fileUrl(url);
    window.open(finalUrl, '_blank');
  };

  const rangoActivo = useMemo(() => {
    if (!appliedFilters.desde && !appliedFilters.hasta) {
      const fechasDisponibles = casos
        .map((caso) => parseDate(caso.fecha_ingreso))
        .filter(Boolean)
        .sort((a, b) => a - b);

      if (fechasDisponibles.length === 0) {
        return 'Sin fechas disponibles';
      }

      const primeraFecha = formatDateInputValue(fechasDisponibles[0]);
      const ultimaFecha = formatDateInputValue(fechasDisponibles[fechasDisponibles.length - 1]);

      return `${formatDateLabel(primeraFecha)} a ${formatDateLabel(ultimaFecha)}`;
    }

    if (appliedFilters.desde && appliedFilters.hasta) {
      return `${formatDateLabel(appliedFilters.desde)} a ${formatDateLabel(appliedFilters.hasta)}`;
    }

    if (appliedFilters.desde) {
      return `Desde ${formatDateLabel(appliedFilters.desde)}`;
    }

    return `Hasta ${formatDateLabel(appliedFilters.hasta)}`;
  }, [appliedFilters, casos]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCases / rowsPerPage)),
    [totalCases, rowsPerPage]
  );

  const pageStart = totalCases === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const pageEnd = Math.min(currentPage * rowsPerPage, totalCases);

  useEffect(() => {
    setCurrentPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  useEffect(() => {
    window.localStorage.setItem(
      CASE_TABLE_LAYOUT_STORAGE_KEY,
      JSON.stringify({
        order: columnOrder,
        widths: columnWidths,
      })
    );
  }, [columnOrder, columnWidths]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const activeResize = resizeStateRef.current;
      if (!activeResize) {
        return;
      }

      const nextWidth = Math.max(
        activeResize.minWidth,
        Math.round(activeResize.startWidth + (event.clientX - activeResize.startX))
      );

      setColumnWidths((current) => ({
        ...current,
        [activeResize.columnId]: nextWidth,
      }));
    };

    const handlePointerUp = () => {
      resizeStateRef.current = null;
      document.body.classList.remove('case-table--resizing');
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);

    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
    };
  }, []);

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setCurrentPage(1);
  };

  const togglePendingDeliveryFilter = () => {
    const nextFilters = {
      ...draftFilters,
      pendientesEntrega: !draftFilters.pendientesEntrega,
    };

    setDraftFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setCurrentPage(1);
  };

  const limpiarFiltros = () => {
    setDraftFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setCurrentPage(1);
    setExpandedCases({});
  };

  const resetColumnLayout = () => {
    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
    setHiddenColumns([]);
  };

  const hideColumn = (columnId) => {
    setHiddenColumns((current) => (
      current.includes(columnId) ? current : [...current, columnId]
    ));
  };

  const showColumn = (columnId) => {
    setHiddenColumns((current) => current.filter((item) => item !== columnId));
  };

  const toggleCaseExpansion = (protocoloGeneral) => {
    setExpandedCases((current) => ({
      ...current,
      [protocoloGeneral]: !current[protocoloGeneral],
    }));
  };

  const handleDeleteCase = useCallback(async (protocoloGeneral) => {
    const confirmed = window.confirm(`Eliminar el caso ${protocoloGeneral}? Esta acción no se puede deshacer.`);
    if (!confirmed) {
      return;
    }

    try {
      setDeletingCaseId(protocoloGeneral);
      await axios.delete(apiUrl(`/api/ingresos_casos/${protocoloGeneral}/`), {
        headers: token ? { Authorization: `Token ${token}` } : {},
      });
      setCasos((current) => current.filter((caso) => caso.protocolo_general !== protocoloGeneral));
      setExpandedCases((current) => {
        const next = { ...current };
        delete next[protocoloGeneral];
        return next;
      });
      setTotalCases((current) => Math.max(0, current - 1));
      window.alert('Caso eliminado correctamente.');
    } catch (deleteError) {
      console.error('Error al eliminar caso:', deleteError);
      window.alert('No se pudo eliminar el caso.');
    } finally {
      setDeletingCaseId('');
    }
  }, [token]);

  const columnDefinitions = useMemo(() => {
    const metaById = CASE_TABLE_COLUMNS.reduce((accumulator, column) => {
      accumulator[column.id] = column;
      return accumulator;
    }, {});

    const columnsById = {
      toggle: {
        renderCell: (caso) => (
          caso.muestras?.length > 0 ? (
            <button
              type="button"
              className="case-table__toggle"
              onClick={() => toggleCaseExpansion(caso.protocolo_general)}
              aria-expanded={Boolean(expandedCases[caso.protocolo_general])}
              aria-label={`${expandedCases[caso.protocolo_general] ? 'Ocultar' : 'Mostrar'} muestras del caso ${caso.protocolo_general}`}
            >
              {expandedCases[caso.protocolo_general] ? <FiChevronDown /> : <FiChevronRight />}
            </button>
          ) : null
        ),
      },
      view: {
        renderCell: (caso) => (
          <Link
            to={`/ver-caso/${caso.protocolo_general}`}
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm btn-outline-success"
          >
            Ver caso
          </Link>
        ),
      },
      protocol: { renderCell: (caso) => caso.protocolo_general },
      caseDate: { renderCell: (caso) => formatDateLabel(caso.fecha_caso) },
      entryDate: { renderCell: (caso) => formatDateLabel(caso.fecha_ingreso) },
      owner: { renderCell: (caso) => caso.propietario || 'Sin propietario' },
      species: { renderCell: (caso) => getCaseSpecies(caso) },
      anamnesis: {
        renderCell: (caso) => (
          <span title={caso.antecedentes_anamnesis || ''}>
            {truncateText(caso.antecedentes_anamnesis) || 'Sin antecedentes'}
          </span>
        ),
      },
      primaryDiagnosis: { renderCell: (caso) => caso.diagnostico_primario || 'Sin diagnóstico primario' },
      secondaryDiagnosis: { renderCell: (caso) => caso.diagnostico_secundario || 'Sin diagnóstico secundario' },
      province: { renderCell: (caso) => caso.provincia || 'Sin provincia' },
      locality: { renderCell: (caso) => caso.localidad || 'Sin localidad' },
      address: { renderCell: (caso) => caso.direccion || 'Sin dirección' },
      doctor: { renderCell: (caso) => caso.medico_responsable_nombre || caso.medico_responsable || 'Sin responsable' },
      finalReport: {
        renderCell: (caso) => (
          <div className="d-flex flex-column gap-2">
            <span className={`badge ${caso.archivo_caso ? 'text-bg-success' : 'text-bg-secondary'}`}>
              {caso.archivo_caso ? 'Cargado por usuario' : 'Sin carga'}
            </span>
            <span className={`badge ${caso.informe_final_entregado ? 'text-bg-success' : 'text-bg-warning'}`}>
              {caso.informe_final_entregado ? 'Entregado' : 'Pendiente de entrega'}
            </span>
          </div>
        ),
      },
      documents: {
        renderCell: (caso) => (
          getCaseDocuments(caso).length > 0 ? (
            getCaseDocuments(caso).map((doc, index) => (
              <div key={doc.id || index} className="mb-2">
                <button
                  type="button"
                  className="btn btn-outline-brand btn-sm"
                  onClick={() => descargarArchivo(doc.archivo)}
                >
                  Descargar {doc.nombre || doc.descripcion || getFileName(doc.archivo) || `Documento ${index + 1}`}
                </button>
              </div>
            ))
          ) : (
            <span className="text-muted">No hay documentos</span>
          )
        ),
      },
    };

    if (showActionsColumn) {
      columnsById.actions = {
        renderCell: (caso) => (
          <div className="d-flex gap-2 flex-wrap">
            <Link
              to={`/editar-caso/${caso.protocolo_general}`}
              className="btn btn-outline-brand btn-sm"
            >
              Editar caso
            </Link>
            {canDeleteRecords ? (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleDeleteCase(caso.protocolo_general)}
                disabled={deletingCaseId === caso.protocolo_general}
              >
                {deletingCaseId === caso.protocolo_general ? 'Eliminando...' : 'Eliminar caso'}
              </button>
            ) : null}
          </div>
        ),
      };
    }

    return columnOrder
      .filter((columnId) => columnsById[columnId] && !hiddenColumns.includes(columnId))
      .map((columnId) => ({
        ...metaById[columnId],
        ...columnsById[columnId],
        width: columnWidths[columnId] || metaById[columnId]?.width || 140,
      }));
  }, [canDeleteRecords, columnOrder, columnWidths, deletingCaseId, expandedCases, handleDeleteCase, hiddenColumns, showActionsColumn]);

  const totalColumnCount = columnDefinitions.length;
  const hiddenColumnDefinitions = useMemo(
    () => CASE_TABLE_COLUMNS.filter((column) => hiddenColumns.includes(column.id)),
    [hiddenColumns]
  );

  const handleColumnDragStart = (columnId) => {
    setDraggedColumnId(columnId);
  };

  const handleColumnDrop = (targetColumnId) => {
    if (!draggedColumnId || draggedColumnId === targetColumnId) {
      setDraggedColumnId('');
      return;
    }

    setColumnOrder((current) => moveColumn(current, draggedColumnId, targetColumnId));
    setDraggedColumnId('');
  };

  const handleResizeStart = (event, column) => {
    event.preventDefault();
    event.stopPropagation();
    resizeStateRef.current = {
      columnId: column.id,
      startX: event.clientX,
      startWidth: columnWidths[column.id] || column.width,
      minWidth: column.minWidth || 96,
    };
    document.body.classList.add('case-table--resizing');
  };

  return (
    <section className="container page-shell case-page-layout" aria-busy={loading}>
      {loading ? (
        <div className="case-page-loading-overlay" role="status" aria-live="polite">
          <div className="case-page-loading-overlay__card">
            <span className="inline-loader__spinner" aria-hidden="true" />
            <strong>Cargando casos...</strong>
            <span>Estamos actualizando el listado y los filtros visibles.</span>
          </div>
        </div>
      ) : null}
      <div className="page-header">
        <div>
          <span className="section-kicker">Casos</span>
          <h2 className="page-title">Casos clínicos ingresados</h2>
          <p className="page-subtitle">
            Filtra por protocolo, propietario, diagnóstico, ubicación, responsable y fecha de ingreso.
          </p>
        </div>
        <div className="case-time-pill">
          {loading ? 'Cargando filtros...' : `Mostrando ${totalCases} casos dentro de ${rangoActivo.toLowerCase()}`}
        </div>
      </div>

      <section className="case-list-stats">
        <CaseStatCard
          label="Casos visibles"
          value={loading ? '...' : totalCases}
          detail="Total dentro del filtro activo"
          icon={FiClipboard}
        />
        <CaseStatCard
          label="Con muestras"
          value={loading ? '...' : caseSummary.totalConMuestras}
          detail="Casos con al menos una muestra"
          icon={FiFileText}
        />
        <CaseStatCard
          label="Provincias"
          value={loading ? '...' : caseSummary.totalProvincias}
          detail="Cobertura geográfica visible"
          icon={FiMapPin}
        />
        <CaseStatCard
          label="Documentos"
          value={loading ? '...' : caseSummary.totalDocumentos}
          detail="Archivos asociados en el rango"
          icon={FiCalendar}
        />
      </section>

      <div className="surface-card case-filter-panel">
        <div className="case-filter-panel__top">
          <div>
            <h3 className="form-title mb-2">Filtros de casos</h3>
            <p className="page-subtitle mb-0">
              Combina texto, ubicación y fecha para encontrar rápidamente el caso que buscas.
            </p>
          </div>
          <div className="case-filter-panel__top-side">
            <label className={`case-filter-toggle${appliedFilters.pendientesEntrega ? ' case-filter-toggle--active' : ''}`}>
              <input
                type="checkbox"
                checked={draftFilters.pendientesEntrega}
                onChange={togglePendingDeliveryFilter}
              />
              <span className="case-filter-toggle__box" aria-hidden="true" />
              <span className="case-filter-toggle__content">
                <strong>Solo pendientes de entrega</strong>
                <span>Mostrar únicamente los casos que todavía no fueron entregados.</span>
              </span>
            </label>
            <div className="case-list-toolbar__summary">
              <div className="case-time-bubble">
                <span className="case-time-bubble__label">Rango visible</span>
                <strong>{rangoActivo}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="case-list-toolbar">
          <div className="case-list-filters">
            <label className="case-list-filter">
              <span className="case-list-filter__label">
                Protocolo
                <FilterHelp description="Busca por una parte del protocolo. Ejemplos: HP-111, HP-25, LP-008." />
              </span>
              <input
                type="text"
                className="form-control"
                value={draftFilters.protocolo}
                onChange={(event) => setDraftFilters((current) => ({ ...current, protocolo: event.target.value }))}
                placeholder="Ej.: 26-111 o 24-111"
              />
            </label>

            <label className="case-list-filter">
              <span>Propietario</span>
              <input
                type="text"
                className="form-control"
                value={draftFilters.propietario}
                onChange={(event) => setDraftFilters((current) => ({ ...current, propietario: event.target.value }))}
                placeholder="Ej.: Establecimiento Norte"
              />
            </label>

            <label className="case-list-filter">
              <span className="case-list-filter__label">
                Diagnóstico
                <FilterHelp description="Busca en diagnóstico primario y secundario. Ignora mayúsculas, acentos, espacios y plurales simples." />
              </span>
              <input
                type="text"
                className="form-control"
                value={draftFilters.diagnostico}
                onChange={(event) => setDraftFilters((current) => ({ ...current, diagnostico: event.target.value }))}
                placeholder="Ej.: mastitis cronica"
              />
            </label>

            <label className="case-list-filter">
              <span>Desde</span>
              <input
                type="date"
                className="form-control"
                value={draftFilters.desde}
                max={draftFilters.hasta || undefined}
                onChange={(event) => setDraftFilters((current) => ({ ...current, desde: event.target.value }))}
              />
            </label>

            <label className="case-list-filter">
              <span>Hasta</span>
              <input
                type="date"
                className="form-control"
                value={draftFilters.hasta}
                min={draftFilters.desde || undefined}
                onChange={(event) => setDraftFilters((current) => ({ ...current, hasta: event.target.value }))}
              />
            </label>

            <label className="case-list-filter">
              <span>Provincia</span>
              <select
                className="form-select"
                value={draftFilters.provincia}
                onChange={(event) => {
                  setDraftFilters((current) => ({
                    ...current,
                    provincia: event.target.value,
                    localidad: '',
                  }));
                }}
              >
                <option value="">Todas</option>
                {filterOptions.provincias.map((provincia) => (
                  <option key={provincia} value={provincia}>
                    {provincia}
                  </option>
                ))}
              </select>
            </label>

            <label className="case-list-filter">
              <span>Localidad</span>
              <select
                className="form-select"
                value={draftFilters.localidad}
                onChange={(event) => setDraftFilters((current) => ({ ...current, localidad: event.target.value }))}
              >
                <option value="">Todas</option>
                {filterOptions.localidades.map((localidad) => (
                  <option key={localidad} value={localidad}>
                    {localidad}
                  </option>
                ))}
              </select>
            </label>

            <label className="case-list-filter">
              <span>Médico responsable</span>
              <select
                className="form-select"
                value={draftFilters.medico}
                onChange={(event) => setDraftFilters((current) => ({ ...current, medico: event.target.value }))}
              >
                <option value="">Todos</option>
                {filterOptions.medicos.map((medico) => (
                  <option key={medico.value} value={medico.value}>
                    {medico.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="case-filter-actions">
          <button type="button" className="btn btn-outline-brand" onClick={limpiarFiltros}>
            Limpiar filtro
          </button>
          <button type="button" className="btn btn-brand" onClick={applyFilters}>
            Aplicar
          </button>
        </div>
      </div>

      <div className="surface-card case-list-card">
        <div className="case-list-card__toolbar">
          <div className="case-list-card__controls">
            <label className="case-list-card__rows">
              <span>Filas por página</span>
              <select
                className="form-select"
                value={rowsPerPage}
                onChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </label>
            <button type="button" className="btn btn-outline-brand btn-sm" onClick={resetColumnLayout}>
              Restablecer columnas
            </button>
          </div>
          {hiddenColumnDefinitions.length > 0 ? (
            <div className="case-list-card__hidden-columns">
              <span className="case-list-card__hidden-label">Columnas ocultas</span>
              {hiddenColumnDefinitions.map((column) => (
                <button
                  key={column.id}
                  type="button"
                  className="case-list-card__hidden-chip"
                  onClick={() => showColumn(column.id)}
                >
                  Mostrar {column.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="table-responsive case-table-responsive">
          <table className="table table-striped mt-3 align-middle case-table case-table--compact">
            <colgroup>
              {columnDefinitions.map((column) => (
                <col key={column.id} style={{ width: `${column.width}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columnDefinitions.map((column) => (
                  <th
                    key={column.id}
                    className={`${column.className || ''}${draggedColumnId === column.id ? ' case-table__head-cell--dragging' : ''}`}
                    onDragOver={(event) => {
                      if (column.draggable === false) {
                        return;
                      }
                      event.preventDefault();
                    }}
                    onDrop={() => {
                      if (column.draggable === false) {
                        return;
                      }
                      handleColumnDrop(column.id);
                    }}
                  >
                    <div
                      className={`case-table__head-cell${column.draggable === false ? ' case-table__head-cell--static' : ''}`}
                      draggable={column.draggable !== false}
                      onDragStart={() => handleColumnDragStart(column.id)}
                      onDragEnd={() => setDraggedColumnId('')}
                    >
                      <span className="case-table__head-label">{column.label}</span>
                      {column.removable === false ? null : (
                        <button
                          type="button"
                          className="case-table__hide-column"
                          aria-label={`Ocultar ${column.label}`}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            hideColumn(column.id);
                          }}
                        >
                          ×
                        </button>
                      )}
                      {column.resizable === false ? null : (
                        <button
                          type="button"
                          className="case-table__resize-handle"
                          aria-label={`Cambiar ancho de ${column.label}`}
                          onMouseDown={(event) => handleResizeStart(event, column)}
                        />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={totalColumnCount}>
                    <div className="dashboard-empty dashboard-empty--compact mt-3">
                      <div className="d-flex align-items-center justify-content-center gap-3">
                        <div className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
                        <span>Cargando casos...</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={totalColumnCount}>
                    <div className="alert subtle-alert mb-0 mt-3">{error}</div>
                  </td>
                </tr>
              ) : totalCases === 0 ? (
                <tr>
                  <td colSpan={totalColumnCount}>
                    <div className="dashboard-empty dashboard-empty--compact mt-3">
                      No hay casos dentro del rango seleccionado.
                    </div>
                  </td>
                </tr>
              ) : (
                casos.map((caso) => (
                  <React.Fragment key={caso.protocolo_general}>
                    <tr className={caso.informe_final_entregado ? 'case-table__row case-table__row--delivered' : 'case-table__row case-table__row--pending'}>
                      {columnDefinitions.map((column) => (
                        <td key={`${caso.protocolo_general}-${column.id}`}>
                          {column.renderCell(caso)}
                        </td>
                      ))}
                    </tr>

                    {expandedCases[caso.protocolo_general] && caso.muestras?.flatMap((muestra) => {
                      const sampleRow = (
                        <tr key={muestra.protocolo_muestra} className="table-secondary">
                          <td colSpan={totalColumnCount}>
                            <div className="ps-2">
                              <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                                <strong>Item de muestra</strong>
                                <span className="badge text-bg-light">{muestra.protocolo_muestra}</span>
                                <span className="text-muted">{muestra.sector || 'Sin sector'}</span>
                              </div>
                              <div><strong>INTA CIAP Córdoba tomó la muestra:</strong> {muestra.aisa_tomo_muestra ? 'Sí' : 'No'}</div>
                              {muestra.aisa_tomo_muestra ? (
                                <div>
                                  <strong>Operario INTA:</strong> {muestra.nombre_operario_inta_nombre || muestra.nombre_operario_INTA || 'Sin operario informado'}
                                </div>
                              ) : null}
                              <div><strong>Tipo de muestra:</strong> {muestra.tipo_muestra || 'Sin tipo'}</div>
                              <div><strong>Fecha de recolección:</strong> {formatDateLabel(muestra.fecha_recoleccion)}</div>
                              <div><strong>Observaciones:</strong> {muestra.observaciones || 'Sin observaciones'}</div>
                            </div>
                          </td>
                        </tr>
                      );

                      const resultRows = muestra.resultados?.length > 0
                        ? muestra.resultados.map((res, index) => (
                          <tr key={`${muestra.protocolo_muestra}-resultado-${res.id || index}`} className="table-light">
                            <td colSpan={totalColumnCount}>
                              <div className="ps-5">
                                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                                  <strong>Item de resultado</strong>
                                  <span className="badge text-bg-secondary">Muestra {muestra.protocolo_muestra}</span>
                                </div>
                                <div><strong>Sector:</strong> {res.sector || 'Sin sector'}</div>
                                <div><strong>Tipo análisis:</strong> {res.tipo_analisis || 'Sin tipo'}</div>
                                <div><strong>Resultado:</strong> {res.resultado_texto || 'Sin resultado'}</div>
                                <div><strong>Fecha de registro:</strong> {formatDateTimeLabel(res.fecha_registro)}</div>
                                {res.archivo_adjunto ? (
                                  <div className="mt-2">
                                    <a
                                      href={fileUrl(res.archivo_adjunto)}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Ver archivo adjunto
                                    </a>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))
                        : [
                          (
                            <tr key={`${muestra.protocolo_muestra}-sin-resultados`} className="table-light">
                              <td colSpan={showActionsColumn ? 14 : 13}>
                                <div className="ps-5 text-muted">Esta muestra no tiene resultados registrados.</div>
                              </td>
                            </tr>
                          ),
                        ];

                      return [sampleRow, ...resultRows];
                    })}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="case-list-pagination">
          {!loading && (
            <span className="case-list-pagination__status">
              Mostrando {pageStart}-{pageEnd} de {totalCases} casos
            </span>
          )}
          {!loading && (
            <span className="case-list-pagination__status">
              Página {currentPage} de {totalPages}
            </span>
          )}
          <div className="case-list-pagination__actions">
            <button
              type="button"
              className="btn btn-outline-brand"
              onClick={() => setCurrentPage((current) => Math.max(1, current - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <button
              type="button"
              className="btn btn-brand"
              onClick={() => setCurrentPage((current) => Math.min(totalPages, current + 1))}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </button>
          </div>
        </div>

        <div className="d-flex gap-2 mt-3 flex-wrap">
          <a
            href={apiUrl('/api/exportar-casos/csv/')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <button type="button" className="btn btn-outline-primary">
              Descargar CSV
            </button>
          </a>

          <a
            href={apiUrl('/api/exportar-casos-y-muestras/')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <button type="button" className="btn btn-outline-success">
              Descargar Excel (Casos + Muestras)
            </button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default ListaCasos;
